import { useQueryClient } from '@tanstack/react-query';
import { debounce, isEqual } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';

import {
  queryKeysUser,
  useMutationUpdateCurrentUserSettings,
  useQueryUserData,
} from '~/data/user';

import LocalStorageService from '~/services/localStorage.service';

import { noop } from '@vestigas/utils';

import { type UserSettingsKeyValues } from './userSettingsKeys';

const DEFAULT_DEBOUNCE_TIME = 1000;

type UserSettingsData = Record<string, unknown>;

type P = {
  debounceTime?: number;
  settingsKey: UserSettingsKeyValues | undefined;
  useLocalStorage?: boolean;
};

/**
 * Custom hook to manage and persist user settings either on the server or in localStorage.
 *
 * @param {Object} params - Parameters for the hook.
 * @param {number} [params.debounceTime=DEFAULT_DEBOUNCE_TIME] - Time in milliseconds to debounce the update function.
 * @param {string} params.settingsKey - Key to identify the settings in storage.
 * @param {boolean} [params.useLocalStorage=false] - Flag to determine if localStorage should be used.
 *
 * @returns {Object} - An object containing methods and state for managing user settings.
 * @returns {Function} getUserSettings - Function to retrieve user settings.
 * @returns {Function} persistUserSettings - Function to persist user settings.
 * @returns {boolean} isError - Flag indicating if there was an error.
 * @returns {boolean} isSuccess - Flag indicating if the query was successful.
 * @returns {Error | undefined} error - The error object if an error occurred.
 */
export const usePersistedUserSettings = ({
  debounceTime = DEFAULT_DEBOUNCE_TIME,
  settingsKey,
  useLocalStorage = false,
}: P) => {
  const queryClient = useQueryClient();
  const [localStorageError, setLocalStorageError] = useState<Error | undefined>(
    undefined,
  );
  const [updateError, setUpdateError] = useState<Error | undefined>(undefined);

  const {
    data: userSettings,
    isError: isErrorUserData,
    isSuccess,
    error: queryError,
  } = useQueryUserData(true, {
    enabled: !useLocalStorage && Boolean(settingsKey),
    select: (data: any) =>
      (settingsKey
        ? (data?.settings?.[settingsKey] as UserSettingsData)
        : undefined) ?? {},
  });

  const { mutateAsync: updateUserSettings, error: mutationError } =
    useMutationUpdateCurrentUserSettings();

  /**
   * Get the settings from the user's settings regardless of their storage location (server or localStorage).
   */
  const getUserSettings = useCallback((): UserSettingsData => {
    if (useLocalStorage) {
      try {
        return LocalStorageService.getObjectFromLocalStorage(settingsKey) ?? {};
      } catch (error) {
        setLocalStorageError(
          error instanceof Error ? error : new Error(String(error)),
        );

        return {};
      }
    }

    return userSettings ?? {};
  }, [settingsKey, useLocalStorage, userSettings]);

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (settings: UserSettingsData) => {
        if (!settingsKey) {
          return;
        }

        try {
          await updateUserSettings({
            [settingsKey]: settings,
          });

          setUpdateError(undefined);
        } catch (error) {
          setUpdateError(
            error instanceof Error ? error : new Error(String(error)),
          );
        }
      }, debounceTime),
    [updateUserSettings, settingsKey, debounceTime],
  );

  /**
   * Store the settings in user's settings on the server and optionally in localStorage.
   * Optimistically updates the cache immediately before the debounced server update.
   */
  const persistUserSettings = useCallback(
    (settings: UserSettingsData) => {
      if (!settingsKey) {
        return;
      }

      // No-op when nothing changed: the DataGrid re-fires column events after a
      // settings refetch with the values just persisted; persisting them again
      // creates a request feedback loop that trips rate limits (DNI-860).
      if (isEqual(getUserSettings(), settings)) {
        return;
      }

      if (useLocalStorage) {
        try {
          LocalStorageService.setObjectAsLocalStorage(settingsKey, settings);
          setLocalStorageError(undefined);
        } catch (error) {
          setLocalStorageError(
            error instanceof Error ? error : new Error(String(error)),
          );
        }
      }

      // Optimistically update the cache immediately (before debounce)
      // Must use the exact same query key that useQueryUserData uses
      queryClient.setQueryData(
        queryKeysUser.getData(true),
        (old: Record<string, unknown> | undefined) => {
          if (!old) return old;

          return {
            ...old,
            settings: {
              ...(old.settings as Record<string, unknown> | undefined),
              [settingsKey as string]: settings,
            },
          };
        },
      );

      void debouncedUpdateFunction(settings);
    },
    [
      debouncedUpdateFunction,
      getUserSettings,
      queryClient,
      settingsKey,
      useLocalStorage,
    ],
  );

  const isError =
    isErrorUserData || Boolean(localStorageError) || Boolean(updateError);
  const error = queryError ?? mutationError ?? localStorageError ?? updateError;

  if (!settingsKey) {
    return {
      error: undefined,
      getUserSettings: noop,
      isError: false,
      isSuccess: true,
      persistUserSettings: noop,
    };
  }

  return {
    error,
    getUserSettings,
    isError,
    isSuccess,
    persistUserSettings,
  };
};
